import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import { AxiosError } from "axios";

export interface Registration {
    registration_id: number;
    participant_id: number;
    period_id: number;
    package_id: number;
    registration_date?: string;
    notes?: string | null;
    created_at?: string;
    updated_at?: string;
    package?: {
        package_id: number;
        period_id: number;
        parent_id?: number | null;
        code: string;
        name: string;
        description: string;
        price: string;
        is_quota: number;
        quota: number;
        is_header: number;
        is_active: number;
        photo_path?: string;
    };
    period?: {
        period_id: number;
        name: string;
        start_date: string;
        end_date: string;
        is_active: number;
    };
}

export interface RegistrationPayload {
    period_id: number;
    package_id: number;
    note?: string; // frontend field
}

export function useUserPackageRegistration(participantId: number, periodId: number) {
    const queryClient = useQueryClient();

    // ==========================
    // 🟢 CREATE REGISTRATION
    // ==========================
    const createRegistration = useMutation<Registration, AxiosError, RegistrationPayload>({
        mutationFn: async (payload) => {
            const { data } = await axiosClient.post<Registration>(
                `/participants/${participantId}/registrations`,
                payload
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["participant-registrations", participantId],
            });
            toast.success("Registrasi berhasil dibuat!");
        },
        onError: (error) => {
            const message =
                (error.response?.data as { message?: string })?.message ??
                error.message ??
                "Gagal membuat registrasi.";
            toast.error(message);
        },
    });

    // ==========================
    // 🟢 UPDATE REGISTRATION
    // ==========================
    const updateRegistration = useMutation<
        Registration,
        AxiosError,
        { registrationId: number; payload: RegistrationPayload }
    >({
        mutationFn: async ({ registrationId, payload }) => {
            const { data } = await axiosClient.put<Registration>(
                `/participants/${participantId}/registrations/${registrationId}`,
                payload
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["participant-registrations", participantId],
            });
            toast.success("Registrasi berhasil diperbarui!");
        },
        onError: (error) => {
            const message =
                (error.response?.data as { message?: string })?.message ??
                error.message ??
                "Gagal memperbarui registrasi.";
            toast.error(message);
        },
    });

    // ==========================
    // 🟣 SAVE OR UPDATE (AUTO)
    // ==========================

    const saveOrUpdateRegistration = useMutation<Registration, AxiosError, RegistrationPayload>({
        mutationFn: async (payload): Promise<Registration> => {
            // ✅ Gunakan data cache dulu kalau ada
            let existing = queryClient.getQueryData<Registration | null>([
                "participant-registrations",
                participantId,
                periodId,
            ]) ?? null;

            // Jika belum ada di cache, baru fetch dari server
            if (!existing) {
                try {
                    const response = await axiosClient.get<{ data: Registration | null }>(
                        `/participants/${participantId}/registrations/period/${periodId}`
                    );
                    existing = response.data.data;
                } catch (error) {
                    const axiosError = error as AxiosError;
                    if (axiosError.response?.status !== 404) throw axiosError;
                }
            }

            const payloadFixed = {
                ...payload,
                notes: payload.note ?? null,
            };

            if (existing?.registration_id) {
                const { data } = await axiosClient.put<Registration>(
                    `/participants/${participantId}/registrations/${existing.registration_id}`,
                    payloadFixed
                );
                return data;
            } else {
                const { data } = await axiosClient.post<Registration>(
                    `/participants/${participantId}/registrations`,
                    payloadFixed
                );
                return data;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["participant-registrations", participantId, periodId],
            });
        },
        onError: (error) => {
            const message =
                (error.response?.data as { message?: string })?.message ??
                error.message ??
                "Terjadi kesalahan saat menyimpan paket.";
            toast.error(message);
        },
    });

    return { createRegistration, updateRegistration, saveOrUpdateRegistration };
}
